import type { ThemeOptions } from '@mui/material/styles';
// Registers the x-date-pickers component keys (MuiPickersDay, …) on ThemeOptions['components'].
import type {} from '@mui/x-date-pickers/themeAugmentation';

import colors from './tailwind/colors';

// `darker` is the app's teal accent, not a darker primary -- different hue, and lighter. The same
// colour is also `teal.700` and `$buttonLink` plus ~18 hardcoded copies, so renaming it is a
// colour-unification change, not an edit here. Both halves of the augmentation are load-bearing and
// for different reasons: the options interface admits the key in the palette literal below, and
// PaletteColor admits `theme.palette.primary.darker` in a .ts read. Neither makes a read safe.
declare module '@mui/material/styles' {
  interface PaletteColor {
    // Optional, because only `primary` defines it: required-typing would make `error.darker`
    // type-check and resolve undefined at runtime, and would buy nothing in exchange -- every
    // runtime read is in a .js/.jsx file, which tsconfig excludes via checkJs: false.
    darker?: string;
  }
  interface SimplePaletteColorOptions {
    darker?: string;
  }
}

// Typed as ThemeOptions deliberately: a component key or slot that MUI does not have becomes a
// compile error rather than silently-dead config. It only checks that the config is well-formed,
// never that it is correct: a slot can stay valid while the conditions under which it applies
// change, and values are not checked at all -- so a compiling theme is not a working one. The
// blocks where that gap matters are marked inline below.
const themeOptions: ThemeOptions = {
  spacing: 6,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1366,
      xl: 1920,
    },
  },
  palette: {
    background: {
      default: '#F8F8F8',
    },
    primary: {
      main: '#305a7f', // TODO - update secondary
      darker: '#1A9C9E',
    },
    secondary: {
      main: '#c00b4b',
    },
    // Pinned to the token module's `baseColors` because unpinned, MUI supplies its own red, green
    // and blue instead of the app's. The coupling is to those base shades, not to the `status.*`
    // aliases built on them, so re-pointing an alias does not reach the theme. Two mismatches worth
    // knowing before "fixing" either: `warning` reads the shade that `status.pending` aliases
    // (`status.warning` is `orange[700]`, a different colour) and is kept because it is
    // byte-identical to the previous `warning.main`; and `info` has no Status counterpart at all,
    // #339CFD being that palette's only blue, named Action there.
    error: {
      main: colors.red[700],
    },
    success: {
      main: colors.green[700],
    },
    info: {
      main: colors.blueSky[700],
    },
    warning: {
      main: colors.yellow[700],
    },
  },
  typography: {
    fontFamily: ['"Poppins"', 'sans-serif'].join(','),
    h6: {
      color: '#202020',
      fontSize: '18px',
      letterSpacing: '-0.4px',
      lineHeight: '30px',
      fontWeight: 600,
    },
    h5: {
      color: '#202020',
    },
    subtitle1: {
      fontWeight: 600,
      letterSpacing: '-0.3px',
      lineHeight: '24px',
      color: '#202020',
    },
    body1: {
      color: '#202020',
      fontSize: 14,
    },
    body2: {
      color: '#202020',
      fontSize: 13,
    },
    caption: {
      fontWeight: 600,
      opacity: 0.5,
      letterSpacing: '-0.26px',
      lineHeight: '18px',
      color: '#202020',
    },
  },
  components: {
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        rounded: {
          borderRadius: '6px',
        },
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
    },
    // The Autocomplete's list renders a Paper without an elevation of its own, so the flat default
    // above left it with no shadow. Select menus and date pickers pass elevation 8, and the list
    // gets the same shadow so every option list lifts off the page alike.
    MuiAutocomplete: {
      styleOverrides: {
        paper: ({ theme }) => ({
          boxShadow: theme.shadows[8],
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        // v5 dropped the inner `wrapper` span; these are the wrapper's styles folded into `root`.
        root: {
          // Flat fold of v4's wrapper + root padding.
          padding: '16px 47px',
          lineHeight: '24px',
          fontSize: 16,
          fontWeight: 'normal',
          textTransform: 'none',
          // No `color` here on purpose: MUI collapses the slots into one styled root that
          // `styleOverrides.root` patches last, so a colour here would outrank `textColorInherit`
          // below. The `textColor="primary"` strips take MUI's own `text.secondary` instead.
          '&.Mui-selected': {
            fontWeight: 'bold',
            borderBottom: '1px solid #305a7f',
          },
        },
        // Applies only to <Tabs textColor="inherit">; every other strip passes textColor="primary"
        // and takes MUI's own width and colours. Do NOT mirror onto textColorPrimary — those strips
        // (InnerTabs among them) have never had this width or these selected styles, and their
        // unselected label colour is deliberately MUI's `text.secondary`. The flat 220px is enough
        // on its own: MUI's own minWidth is flat too, and this slot composes after root.
        textColorInherit: {
          minWidth: '220px',
          opacity: 1,
          fontWeight: 400,
          fontSize: '16px',
          marginLeft: '24px',
          '&.Mui-selected': {
            backgroundColor: '#FFFFFF',
            color: '#202020',
            textTransform: 'none',
            fontWeight: 600,
            borderTop: '5px solid #32C7CA',
            borderBottom: 'none',
            borderRadius: '5px 5px 0px 0px',
          },
        },
      },
    },
    MuiCssBaseline: {
      // v5 drops the `@global` wrapper -- the selectors sit directly under styleOverrides.
      styleOverrides: {
        '*::-webkit-scrollbar': {
          width: '20px',
        },
        '*::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: '#d6dee1',
          borderRadius: '20px',
          border: '6px solid transparent',
          backgroundClip: 'content-box',
        },
        '*::-webkit-scrollbar-thumb:hover': {
          backgroundColor: '#a8bbbf',
        },
        body: {
          fontFamily: 'Poppins',
          fontSize: 14,
          letterSpacing: '-0.3px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        // v5 removed the inner `label` span, so its typography folds into `root`.
        root: {
          height: '32px',
          fontWeight: 600,
          fontSize: '14px',
          letterSpacing: '-0.3px',
          lineHeight: '23px',
        },
        textPrimary: {
          color: '#1A9C9E',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: 13,
          height: '50px',
        },
        head: {
          height: '50px',
          lineHeight: '18px',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          opacity: 1,
          fontWeight: 400,
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        fontSizeSmall: {
          fontSize: '15px',
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        // PickersDay spreads `theme.typography.caption`, whose app value carries `opacity: 0.5` and
        // `fontWeight: 600`; reset both so day numbers render solid and normal-weight. (The selected
        // day keeps MUI's own 500 emphasis, and the weekday labels keep the muted caption look.)
        root: {
          opacity: 1,
          fontWeight: 400,
        },
        // The adjacent-month spacer (PickersDayFiller) shares the MuiPickersDay Root slot and hides
        // itself with `opacity: 0`; the `opacity: 1` above would un-hide it — a stray today/selected
        // circle in the neighbouring month's grid. Re-hide the filler slot (its override is composed
        // after root, so it wins for the filler while root still applies to real days).
        hiddenDaySpacingFiller: {
          opacity: 0,
        },
      },
    },
    PrivatePickersYear: {
      styleOverrides: {
        // Soften the year button from the app's subtitle1 weight (600) to 500. MUI mis-keys this
        // slot: the class key is `yearButton` but the overridesResolver reads `styles.button`, so
        // only `button` applies at runtime — hence the @ts-expect-error (drop it if MUI reconciles
        // the two and it starts erroring as unused).
        // @ts-expect-error runtime override key is `button`, not the typed `yearButton`
        button: {
          fontWeight: 500,
          // v5 colours a disabled year with `text.secondary` (0.6); use `text.disabled` (0.38).
          '&.Mui-disabled': {
            color: 'rgba(0, 0, 0, 0.38)',
          },
        },
      },
    },
    PrivatePickersMonth: {
      styleOverrides: {
        // Mirror PrivatePickersYear: soften the weight and dim a disabled month to text.disabled (0.38).
        root: {
          fontWeight: 500,
          // `&:disabled`, not `&.Mui-disabled` as PrivatePickersYear uses: PickersMonth renders a native
          // <button disabled> and composes only `root`/`selected`, so no Mui-disabled class is ever
          // emitted and the class selector would be dead.
          '&:disabled': {
            color: 'rgba(0, 0, 0, 0.38)',
          },
        },
      },
    },
    MuiClock: {
      styleOverrides: {
        // The AM/PM labels are `<Typography variant="caption">`, so they carry the app caption's
        // `color`/`opacity: 0.5` and override the button's own colour (white when its meridiem is
        // selected). Inherit the button colour at full opacity so the selected meridiem reads white.
        amButton: {
          '& .MuiTypography-root': {
            color: 'inherit',
            opacity: 1,
          },
        },
        pmButton: {
          '& .MuiTypography-root': {
            color: 'inherit',
            opacity: 1,
          },
        },
        // Keyboard-active affordance: when the clock listbox has :focus-visible (arrow keys move the
        // hand), ring the pointer thumb so keyboard users can see the active control.
        clock: {
          '&:has(.MuiClock-wrapper:focus-visible) .MuiClockPointer-thumb': {
            outline: '1px solid #FFFFFF',
            outlineOffset: '-3px',
          },
        },
      },
    },
    // No pickers-toolbar override: the desktop x-date-pickers render no toolbar to style.
  },
};

export default themeOptions;
